/**
 * Presentation Mode sequence.
 *
 * The recorded presentation follows its own journey, independent of the Explore Mode chapters.
 * Each section names the scenes it shows, in order; every scene still lives in a site chapter,
 * so the site stays complete and the presentation stays lean.
 *
 *   1. Merlion IT corporate leadership      6. The LAMS products
 *   2. Project team and alignment           7. Integration
 *   3. The District challenge               8. Project plan
 *   4. EAM concept and the Property model   9. Investment in building the platform
 *   5. Technical architecture              10. Technical support
 */
export interface PresentationSection {
  id: string
  /** Shown on the stage header and in the presenter view. */
  title: string
  /** Spoken transition into the section. Presenter view only — never on the recorded stage. */
  transitionIn?: string
  sceneIds: string[]
}

export const presentationSections: PresentationSection[] = [
  {
    id: 'opening',
    title: 'LAMS',
    sceneIds: ['OPEN-01'],
  },
  {
    id: 'leadership',
    title: 'Merlion IT · Corporate Leadership',
    transitionIn:
      'Before the project, the company. The RFP asked for the project staff, and we will present them next — but first, the leadership that directs Merlion IT and supports every engagement it takes on.',
    sceneIds: ['TEAM-01', 'TEAM-02', 'TEAM-03', 'TEAM-04', 'TEAM-05', 'TEAM-06'],
  },
  {
    id: 'project-team',
    title: 'Project Team & Alignment',
    transitionIn:
      'That is the company. Now the team the proposal names for LAMS — unchanged — and how each role aligns with the project.',
    sceneIds: ['TEAM-07', 'TEAM-08', 'TEAM-10', 'TEAM-11'],
  },
  {
    id: 'challenge',
    title: 'The District Challenge',
    transitionIn:
      'Everything that follows answers one operational reality at the District. Let us name it first.',
    sceneIds: ['CH-03'],
  },
  {
    id: 'eam',
    title: 'Enterprise Asset Management',
    transitionIn:
      'Our answer starts with a discipline, not a product. We are proposing to apply Enterprise Asset Management to the District\'s land — so first, what EAM is, why public land needs more than the conventional version, why this discipline rather than another, and what it looks like on a real Property.',
    sceneIds: ['EAM-01', 'EAM-02', 'EAM-03', 'EAM-04', 'EAM-07'],
  },
  {
    id: 'architecture',
    title: 'Technical Architecture',
    transitionIn:
      'With the business model clear, the technical architecture: the elements that deliver it, and the stack it runs on.',
    sceneIds: ['ARCH-01', 'ARCH-03'],
  },
  {
    id: 'products',
    title: 'The LAMS Products',
    transitionIn:
      'That architecture is delivered as three products that work together — Studio, Runtime and Operations — each with modules that answer a specific part of the District\'s demand.',
    sceneIds: ['PROD-02', 'PROD-03', 'PROD-04', 'SOL-03'],
  },
  {
    id: 'integration',
    title: 'Integration',
    transitionIn:
      'LAMS does not stand alone. This is how it works with CivicPlus, ArcGIS and the rest of the District\'s systems.',
    sceneIds: ['ARCH-04'],
  },
  {
    id: 'plan',
    title: 'Project Plan',
    transitionIn:
      'A strong architecture only matters if it can be delivered predictably. First the method, then the plan.',
    sceneIds: ['DEL-01', 'DEL-02', 'DEL-03', 'DEL-04', 'DEL-06', 'DEL-07', 'DEL-08'],
  },
  {
    id: 'investment',
    title: 'Investment',
    transitionIn:
      'The investment to build the platform follows that plan: a not-to-exceed price, paid against accepted milestones, with consequences if we miss.',
    sceneIds: ['INV-01', 'INV-02', 'INV-04'],
  },
  {
    id: 'support',
    title: 'Technical Support',
    transitionIn:
      'Go-live is where operations begin. The last part of the proposal is how we support LAMS for the first twelve months, and what that costs.',
    sceneIds: ['DEL-09', 'DEL-10', 'INV-05'],
  },
  {
    id: 'closing',
    title: 'LAMS',
    transitionIn:
      'What SRWMD receives is not simply a new database — it is a governed operational environment for the full land lifecycle, from a company and a team that know the discipline behind it.',
    sceneIds: ['CLOSE-01'],
  },
]
